import React, { useEffect, useRef, useState } from 'react';
import { Alert, Button, Form, Spinner } from 'react-bootstrap';

import Logo from '../Logo/Logo';

export type Props = {
  onVerify: (code: string) => Promise<void>;
  onResend: () => Promise<void>;
  onLogout: () => Promise<void>;
};

const RESEND_COOLDOWN_SEC = 60;

export const TwoFactorVerify: React.FC<Props> = ({
  onVerify,
  onResend,
  onLogout,
}) => {
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [resendLoading, setResendLoading] = useState(false);
  const [sendingInitial, setSendingInitial] = useState(true);
  const [sendError, setSendError] = useState(false);
  const [logoutLoading, setLogoutLoading] = useState(false);
  const initialSentRef = useRef(false);

  useEffect(() => {
    if (initialSentRef.current) {
      return;
    }
    initialSentRef.current = true;
    onResend()
      .catch(() => setSendError(true))
      .finally(() => setSendingInitial(false));
  }, [onResend]);

  useEffect(() => {
    if (resendCooldown <= 0) {
      return;
    }
    const timer = setTimeout(() => setResendCooldown(prev => prev - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const handleVerify = async () => {
    if (!code || code.length !== 6) {
      setError('Wpisz 6-cyfrowy kod.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await onVerify(code);
    } catch (err: unknown) {
      setError(
        err instanceof Error ? err.message : 'Nieprawidłowy kod weryfikacyjny.',
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResend = async () => {
    setError('');
    setSendError(false);
    setResendLoading(true);
    try {
      await onResend();
      setResendCooldown(RESEND_COOLDOWN_SEC);
    } catch {
      setError('Nie udało się wysłać kodu. Spróbuj ponownie.');
    } finally {
      setResendLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      void handleVerify();
    }
  };

  if (sendingInitial) {
    return (
      <main className="form-centered text-center">
        <Logo className="mb-3 mt-5" />
        <Spinner animation="border" />
        <p className="mt-2">Wysyłanie kodu...</p>
      </main>
    );
  }

  return (
    <main className="form-centered">
      <Logo className="mb-3 mt-5" />
      <h3 className="fw-normal">Weryfikacja dwuetapowa</h3>
      <p className="text-muted mt-2">
        Sprawdź skrzynkę e-mail. Wpisz 6-cyfrowy kod weryfikacyjny.
      </p>

      {sendError && !error && (
        <Alert variant="warning">
          Jeśli masz kod weryfikacyjny, wpisz go poniżej. W przeciwnym razie
          wyślij go ponownie.
        </Alert>
      )}
      {error && <Alert variant="danger">{error}</Alert>}

      <Form.Group className="form-floating mt-3" controlId="tfa-code">
        <Form.Control
          type="text"
          inputMode="numeric"
          pattern="[0-9]{6}"
          maxLength={6}
          placeholder="Kod weryfikacyjny"
          value={code}
          onChange={e => setCode(e.target.value.replace(/\D/g, ''))}
          onKeyDown={handleKeyDown}
          autoComplete="off"
          autoFocus
        />
        <Form.Label>Kod weryfikacyjny</Form.Label>
      </Form.Group>

      <Button
        type="button"
        className="w-100 btn btn-lg btn-login mt-3"
        onClick={handleVerify}
        disabled={loading}
      >
        {loading ? <Spinner animation="border" size="sm" /> : 'Weryfikuj'}
      </Button>

      <Button
        className="btn-link-click mt-2"
        onClick={handleResend}
        disabled={resendLoading || resendCooldown > 0}
      >
        {resendCooldown > 0
          ? `Wyślij ponownie (${resendCooldown}s)`
          : 'Wyślij kod ponownie'}
      </Button>

      <Button
        className="btn-link-click mt-1"
        disabled={logoutLoading}
        onClick={() => {
          setLogoutLoading(true);
          void onLogout().finally(() => setLogoutLoading(false));
        }}
      >
        {logoutLoading ? (
          <Spinner animation="border" size="sm" />
        ) : (
          'Zaloguj się na inne konto'
        )}
      </Button>
    </main>
  );
};
